import { motion } from "framer-motion";
import { Printer, LoaderPinwheel, BookOpen, Paperclip, Frame, Sticker, ShieldCheck, ArrowUpRight } from "lucide-react";
import Reveal, { CmykDots } from "@/components/Reveal";
import { scrollToId } from "@/App";

const SERVICES = [
  {
    icon: Printer,
    name: "Document Printing",
    desc: "Black & white or colour, A4 and A3, on clean 70 GSM stock. Reports, decks, invoices, resumes and legal bundles.",
    price: "from ₹6 / page",
    image: "https://images.unsplash.com/photo-1585811759562-a30733b277f1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    alt: "Premium paper stacks ready for document printing",
    wide: true,
  },
  {
    icon: LoaderPinwheel,
    name: "Spiral Binding",
    desc: "Lies flat, opens fully. Up to 125 sheets with clear front and board back.",
    price: "₹160",
  },
  {
    icon: BookOpen,
    name: "Wiro Binding",
    desc: "Metal wire, lay-flat finish for manuals, menus and presentations.",
    price: "₹160",
  },
  {
    icon: Frame,
    name: "Thesis & Hard Cover",
    desc: "Hardbound theses and dissertations with foil-stamped titles. Quoted per specification.",
    price: "on quote",
    image: "https://images.unsplash.com/photo-1630448925000-f014372affc7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    alt: "Hardcover and wiro bound documents",
    wide: true,
  },
  {
    icon: Paperclip,
    name: "Staple & Pin Finishing",
    desc: "Corner pins and side staples for handouts and exam papers.",
    price: "from ₹24",
  },
  {
    icon: Sticker,
    name: "UV Stickers",
    desc: "Custom UV-printed stickers — sharp colour, waterproof and scratch-resistant. Any shape, cut to size.",
    price: "on quote",
    image: "https://images.unsplash.com/photo-1664289192124-f0d784151d11?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    alt: "Colourful printed vinyl stickers",
  },
  {
    icon: ShieldCheck,
    name: "UV Direct Protected Print",
    desc: "Direct UV printing with a protective coating — rich, durable colour on boards, signage and giveaway items.",
    price: "on quote",
  },
];

function Tile({ s, i }) {
  const Icon = s.icon;
  return (
    <motion.article
      data-testid={`service-tile-${i}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.5, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -6 }}
      className={`group relative h-full border border-edge bg-surface rounded-sm overflow-hidden hover:border-cyanbrand/40 transition-colors duration-300 flex flex-col ${
        s.wide ? "md:col-span-2" : ""
      }`}
    >
      {s.image && (
        <div className="relative h-44 overflow-hidden">
          <img
            src={s.image}
            alt={s.alt}
            className="w-full h-full object-cover saturate-[0.8] group-hover:scale-105 transition-transform duration-700"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/20 to-transparent" />
        </div>
      )}
      <div className="p-6 sm:p-7 flex flex-col grow">
        <div className="flex items-center justify-between mb-4">
          <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-cyanbrand">
            <Icon size={15} />
            {String(i + 1).padStart(2, "0")}
          </span>
          <CmykDots className="opacity-40 group-hover:opacity-100 transition-opacity" />
        </div>
        <h3 className="font-display font-semibold text-xl sm:text-2xl text-paper tracking-tight">
          {s.name}
        </h3>
        <p className="mt-3 text-sm text-mist leading-relaxed grow">{s.desc}</p>
        <div className="mt-6 flex items-center justify-between">
          <span className="font-mono text-xs text-yel">{s.price}</span>
          <button
            data-testid={`service-enquire-${i}`}
            onClick={() => scrollToId("contact")}
            className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.2em] text-mist hover:text-cyanbrand transition-colors"
          >
            Enquire <ArrowUpRight size={13} />
          </button>
        </div>
      </div>
    </motion.article>
  );
}

export default function Services() {
  return (
    <section id="services" className="py-20 lg:py-28" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
          <div>
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyanbrand">
              / Services
            </span>
            <h2 className="mt-4 font-display font-semibold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-paper">
              Everything a document needs,
              <br className="hidden sm:block" /> under one roof.
            </h2>
          </div>
          <p className="max-w-sm text-sm text-mist leading-relaxed">
            From a single resume to a department&apos;s semester files — printed, finished
            and packed with care.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 items-stretch">
          {SERVICES.map((s, i) => (
            <Tile key={s.name} s={s} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
